import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { Box, Checkbox, CheckboxProps, Typography } from "@mui/material";
import {
  DataGrid,
  DataGridProps,
  GridOverlay,
  GridSlotProps,
} from "@mui/x-data-grid";
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../utils/utils";
import CustomPaginationV2 from "./CustomPaginationV2";
import { CHART_COLORS, CHART_TYPOGRAPHY } from "./app/provider/patient/chart";
import { ECARE_COLOR } from "@/tokens/ecare-tokens";

type DataGridV2Props = {
  searchString?: string;
  height?: string;
} & DataGridProps;

const CustomNoRowsOverlay = ({ message }: { message: string }) => (
  <GridOverlay>
    <Typography
      sx={{ ...CHART_TYPOGRAPHY.body5, color: CHART_COLORS.textLabel }}
    >
      {message}
    </Typography>
  </GridOverlay>
);

const checkboxSx = {
  height: "15px",
  width: "15px",
  borderRadius: "2.6667px",
  border: "1.5px solid #525252",
};

const UncheckedIcon = () => (
  <Box
    sx={{
      // backgroundColor: "transparent",
      ...checkboxSx,
    }}
  />
);

const CheckedIcon = () => (
  <Box
    sx={{
      ...checkboxSx,
      backgroundColor: "#525252",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <CheckRoundedIcon sx={{ fontSize: 14, color: "#fff" }} />
  </Box>
);

const BaseCheckbox = (props: GridSlotProps["baseCheckbox"]) => (
  <Checkbox
    {...(props as CheckboxProps)}
    icon={<UncheckedIcon />}
    checkedIcon={<CheckedIcon />}
  />
);

const CustomDataGridV2 = (props: DataGridV2Props) => {
  return (
    <Box
      sx={{
        height: props?.height ?? "100%",
        width: props?.style?.width ?? "100%",
        "& .MuiDataGrid-root": {
          border: "none",
          // With no rows there is nothing to paint the body, so the page
          // canvas shows through behind the empty-state overlay.
          backgroundColor: ECARE_COLOR.bg.surface,
          fontFamily: CHART_TYPOGRAPHY.fontFamily,
          ...CHART_TYPOGRAPHY.body5,
        },
        "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader": {
          outline: "none !important",
          backgroundColor: ECARE_COLOR.bg.base,
          borderBottom: `1px solid ${ECARE_COLOR.border.default}`,
          ...CHART_TYPOGRAPHY.body5,
          ...CHART_TYPOGRAPHY.overline,
          color: CHART_COLORS.textTertiary,
        },
        // The grid renders the label in a nested span, so the overline
        // treatment has to be restated here to reach the text itself.
        "& .MuiDataGrid-columnHeaderTitle": {
          ...CHART_TYPOGRAPHY.body5,
          ...CHART_TYPOGRAPHY.overline,
          color: CHART_COLORS.textTertiary,
        },
        "& .MuiDataGrid-columnSeparator": {
          visibility: "hidden !important",
        },
        "& .MuiDataGrid-cell": {
          borderBottom: "none",
          ...CHART_TYPOGRAPHY.body5,
          color: CHART_COLORS.textDark,
          display: "flex",
          alignItems: "center",
        },
        // Rows sit on one white surface and are separated by the cell divider
        // rather than a zebra fill (styleguide §11).
        "& .MuiDataGrid-row.even, & .MuiDataGrid-row.odd": {
          backgroundColor: ECARE_COLOR.bg.surface,
          "&:hover": { backgroundColor: ECARE_COLOR.bg.hover },
        },
        "& .MuiDataGrid-footerContainer": {
          borderTop: `1px solid ${CHART_COLORS.border} !important`,
          minHeight: "50px",
          alignItems: "center",
          backgroundColor: ECARE_COLOR.bg.surface,
        },
        "& .MuiDataGrid-virtualScroller": {
          backgroundColor: ECARE_COLOR.bg.surface,
          "&::-webkit-scrollbar": { width: "10px", height: "10px" },
          "&::-webkit-scrollbar-track": {
            background: CHART_COLORS.surfaceWhite,
          },
          "&::-webkit-scrollbar-thumb": {
            background: CHART_COLORS.border,
            borderRadius: "4px",
            border: "2px solid transparent",
            backgroundClip: "content-box",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#999",
            border: "2px solid transparent",
            backgroundClip: "content-box",
          },
          scrollbarWidth: "thin",
          scrollbarColor: `${CHART_COLORS.border} ${CHART_COLORS.surfaceWhite}`,
          fontSize: `${CHART_TYPOGRAPHY.body5.fontSize} !important`,
        },
      }}
    >
      <DataGrid
        {...props}
        rowHeight={props?.rowHeight ?? 34}
        columnHeaderHeight={28}
        disableRowSelectionOnClick={props?.disableRowSelectionOnClick ?? true}
        paginationMode={props?.paginationMode ?? "server"}
        sortingMode={props?.sortingMode ?? "server"}
        initialState={{
          pagination: {
            paginationModel: {
              ...props?.initialState?.pagination?.paginationModel,
              pageSize:
                props?.initialState?.pagination?.paginationModel?.pageSize ??
                DEFAULT_PAGE_SIZE,
            },
          },
        }}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
        }
        slots={{
          ...props.slots,
          pagination: CustomPaginationV2,
          baseCheckbox: BaseCheckbox,
          noRowsOverlay: () => (
            <CustomNoRowsOverlay
              message={
                props?.searchString
                  ? "No Records Found"
                  : "No Records Available"
              }
            />
          ),
        }}
        slotProps={props.slotProps}
        onRowSelectionModelChange={props.onRowSelectionModelChange}
        rowSelectionModel={props.rowSelectionModel}
        pageSizeOptions={props?.pageSizeOptions ?? PAGE_SIZE_OPTIONS}
      />
    </Box>
  );
};

export default CustomDataGridV2;
